import { fetchWithClerkJwt } from "./fetch-with-clerk";

/** Thrown when **`exchangeCustomerBankPublicToken`** receives a non-OK response. */
export class BankLinkExchangeError extends Error {
  readonly httpStatus: number;
  readonly apiError?: string;
  /** Truncated server **`detail`** (e.g. Straddle JSON error body). */
  readonly detail?: string;

  constructor(httpStatus: number, apiError?: string, detail?: string) {
    const suffix = apiError ? `|${apiError}` : "";
    super(`bank_link_exchange_http_${httpStatus}${suffix}`);
    this.name = "BankLinkExchangeError";
    this.httpStatus = httpStatus;
    this.apiError = apiError;
    this.detail = detail;
  }
}

/** Turns **`exchangeCustomerBankPublicToken`** failures into checkout copy (after Plaid Link succeeds). */
export function formatBankLinkExchangeError(err: unknown): string {
  if (!(err instanceof Error)) {
    return "Could not save your bank link. Try again, or check that the API is running.";
  }
  if (err.message === "clerk_session_missing") {
    return "Sign in again — your session token was missing.";
  }
  if (err instanceof BankLinkExchangeError) {
    const code = err.httpStatus;
    const api = err.apiError;
    if (api === "customer_phone_not_e164") {
      return "Your profile needs a real phone number in international format (e.g. +15551234567) for ACH. Add or fix your phone in Clerk or your account, then try linking again.";
    }
    if (api === "plaid_no_accounts" || api === "plaid_account_not_found") {
      return "Plaid did not return a usable account. Close the bank picker and use “Link bank with Plaid” again; choose a checking account when prompted.";
    }
    if (api === "plaid_exchange_failed") {
      return "Plaid rejected the handoff after Link (token exchange failed). Try linking again; if it keeps failing, confirm PLAID_CLIENT_ID, PLAID_SECRET, and PLAID_ENV on the API match your Plaid dashboard.";
    }
    if (api === "straddle_bank_link_failed") {
      const hint =
        "Confirm STRADDLE_API_BASE_URL (sandbox: https://sandbox.straddle.com, live: https://production.straddle.com), STRADDLE_API_KEY, PLAID_CLIENT_ID / PLAID_SECRET / PLAID_ENV=sandbox, then restart the API. In Plaid Dashboard, ensure the Straddle processor is enabled for processor tokens.";
      if (err.detail != null && err.detail.trim().length > 0) {
        return `Bank link failed: ${err.detail.trim()} ${hint}`;
      }
      return `Bank link failed after Plaid. ${hint}`;
    }
    if (api === "customer_not_found") {
      return "Your customer record was not found. Re-sign in or confirm the API database has a row for this account.";
    }
    if (api === "forbidden") {
      return "You are not allowed to link a bank for this customer id. Confirm checkout is using the same customer as GET /api/v1/me.";
    }
    if (api === "invalid_body") {
      return "The request to save the bank link was invalid. Try again from the Link button.";
    }
    if (code === 401) {
      return "The API rejected your sign-in while saving the bank (401). Sign in again with the same Clerk app the API uses.";
    }
    if (code === 403) {
      return "Not allowed to save this bank link (403). Customer id must match your signed-in profile.";
    }
    if (code === 404) {
      return "Customer not found (404) while saving the bank link. Re-sign in and try again.";
    }
    if (code === 502) {
      const d = err.detail?.trim();
      return d && d.length > 0
        ? `Could not save your bank (502). ${d} Check STRADDLE_* and PLAID_* in services/api/.env and API logs.`
        : "The API could not finish saving your bank (502). Often this is Plaid or Straddle credentials — check API logs and STRADDLE_API_BASE_URL / PLAID_* env vars.";
    }
    if (code >= 500) {
      return `The API hit an error while saving your bank (HTTP ${code}). Check API logs and try again.`;
    }
    if (api) {
      return `Could not save bank link (${api}). Try again or check API logs.`;
    }
    return `Could not save bank link (HTTP ${code}). Try again.`;
  }
  const legacy = /^bank_link_exchange_http_(\d+)(?:\|(.+))?$/.exec(err.message);
  if (legacy) {
    const code = Number(legacy[1]);
    const api = legacy[2];
    return formatBankLinkExchangeError(new BankLinkExchangeError(code, api));
  }
  if (err.message.includes("Failed to fetch") || err.name === "TypeError") {
    return "Could not reach the API while saving your bank (network error). Keep the API running and check VITE_API_BASE_URL if you use a custom host.";
  }
  return err.message || "Could not save your bank link. Try again.";
}

/** Turns **`fetchCustomerBankProfile`** / **`createCustomerBankLinkToken`** failures into checkout copy. */
export function formatBankLinkStepError(err: unknown): string {
  if (!(err instanceof Error)) {
    return "Could not start Plaid. Check that the API is running and try again.";
  }
  if (err.message === "clerk_session_missing") {
    return "Sign in again — your session token was missing.";
  }
  const profileHttp = /^customer_profile_http_(\d+)$/.exec(err.message);
  const tokenHttp = /^bank_link_token_http_(\d+)$/.exec(err.message);
  const code = profileHttp ? Number(profileHttp[1]) : tokenHttp ? Number(tokenHttp[1]) : NaN;
  if (!Number.isNaN(code)) {
    if (code === 401) {
      return "The API rejected your sign-in token (401). Use the same Clerk app for customer-web and the API (publishable + secret keys), and ensure the session is valid.";
    }
    if (code === 403) {
      return "Not allowed to load this customer or link token (403). The URL customer id must match the profile returned from GET /api/v1/me.";
    }
    if (code === 404) {
      return "Customer was not found (404). Re-sign in or confirm your database customer row exists for this Clerk user.";
    }
    if (code === 502) {
      return "Plaid refused to create a link token (502). On the API, set real sandbox values for PLAID_CLIENT_ID and PLAID_SECRET (not placeholders), and PLAID_ENV=sandbox.";
    }
    if (code >= 500) {
      return `API error while starting Plaid (HTTP ${code}). If the API logs show a Plaid error, fix PLAID_* credentials and PLAID_ENV.`;
    }
    return `Request failed (HTTP ${code}).`;
  }
  if (err.message.includes("Failed to fetch") || err.name === "TypeError") {
    return "Could not reach the API (network error). Start the API on port 3000, or set VITE_API_BASE_URL. With Vite dev, /api is proxied — avoid pointing the browser at the wrong host.";
  }
  return err.message || "Could not start Plaid.";
}

export type BankLinkTokenResponse = {
  linkToken: string;
  expiration: string | null;
};

/** Matches **`toPublicCustomer`** fields used after exchange (subset). */
export type CustomerBankLinkProfile = {
  bankAccountLast4: string | null;
  bankName: string | null;
  secrets: {
    plaidItemConfigured: boolean;
    plaidAccessTokenConfigured: boolean;
    straddlePaykeyConfigured: boolean;
  };
};

export async function fetchCustomerBankProfile(
  getToken: () => Promise<string | null>,
  customerId: string,
): Promise<CustomerBankLinkProfile> {
  const path = `/api/v1/customers/${encodeURIComponent(customerId)}`;
  const res = await fetchWithClerkJwt(getToken, path);
  if (!res.ok) {
    throw new Error(`customer_profile_http_${res.status}`);
  }
  return (await res.json()) as CustomerBankLinkProfile;
}

export async function createCustomerBankLinkToken(
  getToken: () => Promise<string | null>,
  customerId: string,
): Promise<BankLinkTokenResponse> {
  const path = `/api/v1/customers/${encodeURIComponent(customerId)}/plaid/bank-link-token`;
  const res = await fetchWithClerkJwt(getToken, path, { method: "POST" });
  if (!res.ok) {
    throw new Error(`bank_link_token_http_${res.status}`);
  }
  return (await res.json()) as BankLinkTokenResponse;
}

export async function exchangeCustomerBankPublicToken(
  getToken: () => Promise<string | null>,
  customerId: string,
  publicToken: string,
  accountId?: string,
): Promise<CustomerBankLinkProfile> {
  const path = `/api/v1/customers/${encodeURIComponent(customerId)}/plaid/bank-link-exchange`;
  const res = await fetchWithClerkJwt(getToken, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      accountId != null && accountId.length > 0 ? { publicToken, accountId } : { publicToken },
    ),
  });
  if (!res.ok) {
    let apiError: string | undefined;
    let detail: string | undefined;
    try {
      const j = (await res.json()) as { error?: unknown; detail?: unknown };
      if (typeof j.error === "string" && j.error.length > 0) {
        apiError = j.error;
      }
      if (typeof j.detail === "string" && j.detail.length > 0) {
        detail = j.detail;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new BankLinkExchangeError(res.status, apiError, detail);
  }
  return (await res.json()) as CustomerBankLinkProfile;
}
